import { useState, useCallback, useEffect, useRef } from 'react';
import type { AppShellProps, OfflineBannerStatus, BreakpointMode } from './types';
import { SkipToContent } from './SkipToContent';
import { OfflineBanner } from './OfflineBanner';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';

// ─── Breakpoint hook ────────────────────────────────────────────

function useBreakpointMode(): BreakpointMode {
  const [mode, setMode] = useState<BreakpointMode>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    const mdQuery = window.matchMedia('(min-width: 768px)');
    const lgQuery = window.matchMedia('(min-width: 1024px)');

    function update() {
      if (lgQuery.matches) setMode('desktop');
      else if (mdQuery.matches) setMode('tablet');
      else setMode('mobile');
    }

    mdQuery.addEventListener('change', update);
    lgQuery.addEventListener('change', update);
    return () => {
      mdQuery.removeEventListener('change', update);
      lgQuery.removeEventListener('change', update);
    };
  }, []);

  return mode;
}

// ─── Derive OfflineBanner status from AppShell-level props ──────

function deriveOfflineBannerStatus(
  offlineStatus?: { isOffline: boolean; queueCount: number; isSyncing: boolean },
  syncConflicts?: number,
): OfflineBannerStatus {
  if (syncConflicts && syncConflicts > 0) return 'conflict';
  if (offlineStatus?.isSyncing) return 'syncing';
  if (offlineStatus?.isOffline) return 'offline';
  return 'online';
}

// ─── Shared OfflineBanner rendering helper ──────────────────────

function renderOfflineBanner(
  offlineStatus: AppShellProps['offlineStatus'],
  syncConflicts: AppShellProps['syncConflicts'],
  breakpoint: BreakpointMode,
) {
  const bannerStatus = deriveOfflineBannerStatus(offlineStatus, syncConflicts);
  return (
    <OfflineBanner
      status={bannerStatus}
      queueCount={offlineStatus?.queueCount}
      conflictCount={syncConflicts}
      breakpoint={breakpoint}
    />
  );
}

// ─── AppShell Component ─────────────────────────────────────────

export function AppShell({
  variant = 'default',
  children,
  // Sidebar
  sidebarCollapsed = false,
  onSidebarToggle,
  sidebarItems = [],
  activePath,
  onNavigate,
  sidebarDoubleClickToToggle,
  sidebarExpandedGroups,
  onSidebarExpandedGroupsChange,
  // User & Tenant
  user,
  tenant,
  featureFlags,
  // Header
  title,
  breadcrumbs,
  headerActions,
  // Location
  locations,
  activeLocation,
  onLocationChange,
  showAllLocations,
  allLocationsLabel,
  // Notifications
  notificationCount,
  onNotificationClick,
  // User menu
  userMenuItems,
  onSignOut,
  // Language
  language,
  onLanguageChange,
  // Offline
  offlineStatus,
  syncConflicts,
  // Mobile
  bottomNavItems = [],
  bottomNavMoreItems,
}: AppShellProps) {
  const breakpoint = useBreakpointMode();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const handleSidebarToggle = useCallback(() => {
    if (breakpoint === 'tablet') {
      setOverlayOpen((prev) => !prev);
    } else {
      onSidebarToggle?.();
    }
  }, [breakpoint, onSidebarToggle]);

  const handleOverlayClose = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  // Close overlay when breakpoint changes away from tablet
  useEffect(() => {
    if (breakpoint !== 'tablet') {
      setOverlayOpen(false);
    }
  }, [breakpoint]);

  // ─── Fullscreen variant ──────────────────────────────────────────
  if (variant === 'fullscreen') {
    return (
      <div className="flex h-screen w-screen flex-col overflow-hidden">
        {renderOfflineBanner(offlineStatus, syncConflicts, breakpoint)}
        <main id="main-content" className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    );
  }

  // ─── Minimal variant (header only) ──────────────────────────────
  if (variant === 'minimal') {
    return (
      <div className="flex h-screen flex-col overflow-hidden">
        <SkipToContent />
        <Header
          title={title}
          breadcrumbs={breadcrumbs}
          tenantName={tenant?.name}
          isOffline={offlineStatus?.isOffline}
          user={user}
          language={language}
          onLanguageChange={onLanguageChange}
          actions={headerActions}
          userMenuItems={userMenuItems}
          onSignOut={onSignOut}
        />
        {renderOfflineBanner(offlineStatus, syncConflicts, breakpoint)}
        <main id="main-content" className="flex-1 overflow-auto bg-neutral-50 dark:bg-neutral-950">
          {children}
        </main>
      </div>
    );
  }

  // ─── Default variant ─────────────────────────────────────────────
  const showSidebar = breakpoint === 'desktop';
  const showBottomNav = breakpoint === 'mobile';
  const showOverlaySidebar = breakpoint === 'tablet' && overlayOpen;

  return (
    <div className="flex h-screen overflow-hidden">
      <SkipToContent />

      {/* Desktop persistent sidebar */}
      {showSidebar && (
        <Sidebar
          items={sidebarItems}
          collapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          activeItem={activePath}
          tenant={tenant}
          user={user}
          featureFlags={featureFlags}
          onNavigate={onNavigate}
          doubleClickToToggle={sidebarDoubleClickToToggle}
          expandedGroups={sidebarExpandedGroups}
          onExpandedGroupsChange={onSidebarExpandedGroupsChange}
        />
      )}

      {/* Tablet overlay sidebar */}
      {showOverlaySidebar && (
        <Sidebar
          items={sidebarItems}
          collapsed={false}
          onToggle={handleOverlayClose}
          activeItem={activePath}
          tenant={tenant}
          user={user}
          featureFlags={featureFlags}
          onNavigate={(path) => {
            onNavigate?.(path);
            setOverlayOpen(false);
          }}
          overlay
          onOverlayClose={handleOverlayClose}
          triggerRef={menuButtonRef}
          expandedGroups={sidebarExpandedGroups}
          onExpandedGroupsChange={onSidebarExpandedGroupsChange}
        />
      )}

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <Header
          breakpoint={breakpoint}
          title={title}
          breadcrumbs={breadcrumbs}
          tenantName={tenant?.name}
          isOffline={offlineStatus?.isOffline}
          notificationCount={notificationCount}
          user={user}
          locations={locations}
          activeLocation={activeLocation}
          onLocationChange={onLocationChange}
          showAllLocations={showAllLocations}
          allLocationsLabel={allLocationsLabel}
          language={language}
          onLanguageChange={onLanguageChange}
          onMenuToggle={handleSidebarToggle}
          onNotificationClick={onNotificationClick}
          actions={headerActions}
          showMenuToggle={breakpoint !== 'desktop'}
          menuButtonRef={menuButtonRef}
          userMenuItems={userMenuItems}
          onSignOut={onSignOut}
        />

        {/* Offline banner */}
        {renderOfflineBanner(offlineStatus, syncConflicts, breakpoint)}

        {/* Page content */}
        <main
          id="main-content"
          className={[
            'flex-1 overflow-auto bg-neutral-50 dark:bg-neutral-950',
            showBottomNav ? 'pb-bottom-nav' : '',
          ].join(' ')}
        >
          {children}
        </main>

        {/* Mobile bottom nav */}
        {showBottomNav && (
          <div className="fixed inset-x-0 bottom-0 z-fixed">
            <BottomNav
              items={bottomNavItems}
              activeItem={activePath}
              onNavigate={onNavigate}
              moreItems={bottomNavMoreItems}
            />
          </div>
        )}
      </div>
    </div>
  );
}
